import { useEffect, useRef, useState } from 'react'
import {
  AlertTriangle, ArrowRight, BadgeCheck, Check, Loader2, Search, UserPlus,
} from 'lucide-react'
import { Modal } from '@/shared/ui/Modal'
import { ErrorStrip, Note } from '@/shared/ui/states'
import { explain } from '@/shared/api/problem'
import { useDebounced } from '@/shared/hooks/useDebounced'
import { cn } from '@/lib/utils'
import { humanise } from '@/shared/lib/format'
import { listUnits } from '@/features/geography/api'
import { listVoters } from '@/features/voters/api'
import { MAX_ID_LENGTH, MIN_ID_LENGTH, idProblem } from '@/features/voters/IdentityLookup'
import type { TransferLookupResponse, UnitResponse } from '@/shared/api/types'
import {
  INTAKE_CHANNEL_LABEL, REGISTRATION_REASON_CODE, TRANSFER_REASON_LABEL,
  TRANSFER_STATUS_LABEL, createTransfer, fieldLookup, lookupByVoterId, placementOf,
} from './api'

/**
 * Raising a request — the field screen.
 *
 * ## It starts with an ID, not a name
 *
 * The person using this is standing at a door with a national register of tens of
 * millions behind them, and the register is full of shared names. A name search asks
 * them to pick the right stranger out of a list; the ID is the number the person in
 * front of them is holding. So the dialog opens as one input, and the rest appears once
 * that input has an answer.
 *
 * Name search still exists, one click away, because an office clerk taking a phone call
 * does not always have an ID number. It is a fallback, not the front door.
 *
 * ## The lookup answers two different questions at once
 *
 * The register is asked first. If the person is on it, we know where they are registered
 * and the work is a **transfer**. If it has never heard of them, the government identity
 * service is asked, and if *that* knows them the work is a **registration** — an eligible
 * citizen nobody has ever put on the roll. Both land in the same queue with the same
 * evidence trail, which is why they share this dialog.
 *
 * That second case is the whole reason this screen was rebuilt: the people a civic drive
 * exists to reach are exactly the people the old name-search intake could not represent.
 *
 * ## What it creates
 *
 * A **DRAFT and nothing more**. Consent comes next, on the request's own page, and the
 * request cannot be submitted until it exists. That order is not a UI preference — it is
 * the property that makes this workflow legitimate, so the button says "Create draft".
 *
 * `rationale` has a 20-character minimum on the server. That friction is deliberate:
 * "moved" is not a case a reviewer can act on, and the reviewer signs their name to it.
 */
export function IntakeDialog({
  onClose, onCreated, voterId: fixedVoterId,
}: {
  onClose: () => void
  onCreated: (id: string) => void
  /** Pre-selected when raising from a voter's own record — skips the lookup. */
  voterId?: string
}) {
  // ── who ──
  const [nationalId, setNationalId] = useState('')
  const [searching, setSearching] = useState(false)
  const [lookup, setLookup] = useState<TransferLookupResponse | null>(null)
  /** Which ID (or voter id) the result on screen belongs to. */
  const [forId, setForId] = useState<string | null>(null)
  const [lookupError, setLookupError] = useState<string | null>(null)
  const [byName, setByName] = useState(Boolean(fixedVoterId))
  const [voterId, setVoterId] = useState(fixedVoterId ?? '')
  const [voterLabel, setVoterLabel] = useState('')

  // ── the citizen block, only for someone the register does not know ──
  const [useUpstream, setUseUpstream] = useState(false)
  const [residenceWardId, setResidenceWardId] = useState('')
  const [residenceWardLabel, setResidenceWardLabel] = useState('')

  // ── what ──
  const [toStationId, setToStationId] = useState('')
  const [toStationLabel, setToStationLabel] = useState('')
  const [reasonCode, setReasonCode] = useState('VOTER_REQUESTED')
  const [intakeChannelCode, setIntakeChannelCode] = useState('FIELD')
  const [voterStatedReason, setVoterStatedReason] = useState('')
  const [rationale, setRationale] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const idFault = idProblem(nationalId)
  const known = lookup?.found === true
  const upstreamOnly = lookup !== null && !lookup.found && Boolean(lookup.upstream?.found)
  const nowhere = lookup !== null && !lookup.found && !lookup.upstream?.found
  const blocked = Boolean(lookup?.openTransferRequestId)

  /**
   * A person the register knows is a transfer. A person only the identity service knows
   * is a registration. The UI never lets the operator choose the kind — it follows from
   * who the person is, and letting them pick would let them pick wrong.
   */
  const kind: 'TRANSFER' | 'REGISTRATION' =
    known && lookup?.registrationStatusCode !== 'UNREGISTERED' ? 'TRANSFER'
      : known ? 'REGISTRATION'
        : upstreamOnly && useUpstream ? 'REGISTRATION'
          : 'TRANSFER'

  const registering = kind === 'REGISTRATION'
  // A subject exists once a LOOKUP has resolved — by ID or by name. Holding a voterId
  // is not enough: without the lookup we do not know their registration status or
  // whether a request is already open for them, and both change what may be raised.
  const haveSubject = (known || (upstreamOnly && useUpstream)) && forId !== null

  const ready =
    haveSubject && !blocked && toStationId !== '' && rationale.trim().length >= 20
    && (!(upstreamOnly && useUpstream) || residenceWardId !== '')

  /**
   * Never trust a result against another ID.
   *
   * The operator corrects a digit while a lookup is in flight; the earlier response
   * lands afterwards and, without this guard, repopulates the screen with a DIFFERENT
   * person while the box shows the new number. The registration path would then bind
   * one person's name to another person's national ID. `seq` drops any response that is
   * not the newest, and `forId` records who the result on screen belongs to.
   * `features/voters/IdentityLookup` solved this first; this is the same guard.
   */
  const seq = useRef(0)
  const alive = useRef(true)
  // `alive.current = true` in the SETUP, not just false in the teardown. React 19's
  // StrictMode mounts, unmounts and remounts every component in development: with only
  // the teardown, the first unmount latches `alive` false for the rest of the dialog's
  // life, every response is discarded, and `setSearching(false)` never runs — a spinner
  // that turns forever in dev and works in a production build. `useLoad` gets this right
  // and this now matches it.
  useEffect(() => {
    alive.current = true
    return () => { alive.current = false }
  }, [])

  const clearSubject = () => {
    setLookup(null); setForId(null); setVoterId(''); setVoterLabel('')
    setUseUpstream(false); setResidenceWardId(''); setResidenceWardLabel('')
  }

  const runLookup = async () => {
    if (idFault) return
    const mine = ++seq.current
    const asked = nationalId.trim()
    setSearching(true); setLookupError(null); clearSubject()
    try {
      const r = await fieldLookup(asked)
      if (!alive.current || seq.current !== mine) return
      setLookup(r); setForId(asked)
      if (r.found && r.voterId) setVoterId(r.voterId)
    } catch (e) {
      if (alive.current && seq.current === mine) setLookupError(explain(e))
    } finally {
      if (alive.current && seq.current === mine) setSearching(false)
    }
  }

  /** A person picked by name goes through the same lookup, so it gets the same rules. */
  const pickByName = async (id: string, label: string) => {
    const mine = ++seq.current
    setVoterId(id); setVoterLabel(label); setLookup(null); setForId(null)
    if (!id) return
    setSearching(true); setLookupError(null)
    try {
      const r = await lookupByVoterId(id)
      if (!alive.current || seq.current !== mine) return
      setLookup(r); setForId(id)
    } catch (e) {
      if (alive.current && seq.current === mine) setLookupError(explain(e))
    } finally {
      if (alive.current && seq.current === mine) setSearching(false)
    }
  }

  const submit = async () => {
    setSaving(true); setError(null)
    try {
      const created = await createTransfer({
        kind,
        // A citizen who is already a row is identified by id; one who is not comes
        // with the block below, and the server tells us which voter it created.
        ...(voterId ? { voterId } : {}),
        ...(upstreamOnly && useUpstream && lookup?.upstream
          ? {
              citizen: {
                nationalId: nationalId.trim(),
                firstName: lookup.upstream.firstName ?? null,
                middleName: lookup.upstream.middleName ?? null,
                lastName: lookup.upstream.surname ?? null,
                birthYear: lookup.upstream.birthYear ?? null,
                genderCode: lookup.upstream.genderCode ?? null,
                residenceWardId,
                // Carries eCitizen's own assertion, so the new row records a
                // government match rather than landing as UNVERIFIED.
                upstreamVerified: lookup.upstream.verified,
              },
            }
          : {}),
        toStationId,
        reasonCode: registering ? REGISTRATION_REASON_CODE : reasonCode,
        intakeChannelCode,
        voterStatedReason: voterStatedReason.trim() || null,
        rationale: rationale.trim(),
      })
      onCreated(created.id)
    } catch (e) { setError(explain(e)); setSaving(false) }
  }

  return (
    <Modal
      open
      onClose={onClose}
      width="lg"
      title={registering ? 'Assist a registration' : 'Raise a transfer request'}
      description={
        registering
          ? 'Creates a draft. The IEBC registers voters — this records the request, its consent and its evidence.'
          : 'Creates a draft. Nothing is submitted, and no record changes, until consent is captured and the request is reviewed.'
      }
      footer={
        <>
          <button type="button" className="erp-btn" onClick={onClose} disabled={saving}>Cancel</button>
          <button
            type="button"
            className="erp-btn erp-btn-primary"
            disabled={!ready || saving}
            onClick={() => void submit()}
          >
            {saving && <Loader2 className="h-3.5 w-3.5 animate-spin" />} Create draft
          </button>
        </>
      }
    >
      <ErrorStrip message={error} className="mb-3 border rounded-md" />

      <div className="space-y-3">
        {/* ── who ─────────────────────────────────────────────────────────── */}
        {!fixedVoterId && (
          byName ? (
            <div>
              <VoterPicker
                value={voterId}
                label={voterLabel}
                loading={searching}
                onPick={(id, label) => void pickByName(id, label)}
              />
              <button
                type="button"
                className="text-erp-xs text-chocolate hover:underline mt-1.5"
                onClick={() => { setByName(false); clearSubject() }}
              >
                Search by national ID instead
              </button>
            </div>
          ) : (
            <div>
              <span className="text-erp-xs text-muted-foreground">
                National ID <span className="text-red-600">*</span>
              </span>
              <div className="mt-1 flex gap-2">
                <input
                  id="intake-national-id"
                  className="erp-input flex-1 font-mono"
                  inputMode="numeric"
                  autoComplete="off"
                  maxLength={MAX_ID_LENGTH}
                  value={nationalId}
                  onChange={(e) => {
                    setNationalId(e.target.value.replace(/\D/g, ''))
                    clearSubject(); setLookupError(null)
                  }}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); void runLookup() } }}
                  placeholder={`${MIN_ID_LENGTH}–${MAX_ID_LENGTH} digits`}
                  aria-label="National ID"
                />
                <button
                  type="button"
                  className="erp-btn"
                  disabled={Boolean(idFault) || searching}
                  onClick={() => void runLookup()}
                >
                  {searching
                    ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    : <Search className="h-3.5 w-3.5" />} Search
                </button>
              </div>
              <div className="flex items-start justify-between gap-3 mt-1">
                <p className="text-erp-xs text-muted-foreground">
                  {nationalId && idFault
                    ? idFault
                    : 'Checked against our register first, then the government identity service.'}
                </p>
                <button
                  type="button"
                  className="text-erp-xs text-chocolate hover:underline shrink-0"
                  onClick={() => { setByName(true); setNationalId(''); clearSubject() }}
                >
                  No ID to hand?
                </button>
              </div>
            </div>
          )
        )}

        <ErrorStrip message={lookupError} className="border rounded-md" />

        {/* ── what the lookup found ───────────────────────────────────────── */}
        {lookup && (
          <SubjectCard
            lookup={lookup}
            useUpstream={useUpstream}
            onUseUpstream={setUseUpstream}
            residenceWardId={residenceWardId}
            residenceWardLabel={residenceWardLabel}
            onPickWard={(id, label) => { setResidenceWardId(id); setResidenceWardLabel(label) }}
          />
        )}

        {nowhere && (
          <Note tone="bad">
            Neither our register nor the identity service has this ID. There is nothing to raise a
            request about — check the digits with the person in front of you before trying again.
          </Note>
        )}

        {/* ── what is being asked for ─────────────────────────────────────── */}
        {haveSubject && !blocked && (
          <>
            <StationPicker
              title={registering ? 'Register at which polling station?' : 'Target polling station'}
              value={toStationId}
              label={toStationLabel}
              onPick={(id, label) => { setToStationId(id); setToStationLabel(label) }}
            />

            <div className="grid grid-cols-2 gap-3">
              {registering ? (
                <div>
                  <span className="text-erp-xs text-muted-foreground">Reason</span>
                  <p className="erp-input w-full mt-1 flex items-center text-muted-foreground">
                    Not yet registered
                  </p>
                </div>
              ) : (
                <label className="block">
                  <span className="text-erp-xs text-muted-foreground">Reason</span>
                  <select className="erp-input w-full mt-1" value={reasonCode}
                          onChange={(e) => setReasonCode(e.target.value)}>
                    {Object.entries(TRANSFER_REASON_LABEL)
                      .filter(([c]) => c !== REGISTRATION_REASON_CODE)
                      .map(([c, l]) => <option key={c} value={c}>{l}</option>)}
                  </select>
                </label>
              )}
              <label className="block">
                <span className="text-erp-xs text-muted-foreground">How it reached us</span>
                <select className="erp-input w-full mt-1" value={intakeChannelCode}
                        onChange={(e) => setIntakeChannelCode(e.target.value)}>
                  {Object.entries(INTAKE_CHANNEL_LABEL)
                    .filter(([c]) => c !== 'SYSTEM_SUGGESTION')   // only Phase 5 creates those
                    .map(([c, l]) => <option key={c} value={c}>{l}</option>)}
                </select>
              </label>
            </div>

            <label className="block">
              <span className="text-erp-xs text-muted-foreground">
                {registering ? 'What they said' : "The voter's own words"}{' '}
                <span className="opacity-70">(optional)</span>
              </span>
              <textarea
                className="erp-input w-full mt-1 min-h-[56px]"
                value={voterStatedReason}
                onChange={(e) => setVoterStatedReason(e.target.value)}
                placeholder={registering
                  ? '“Nobody came to my area the last time and I have never voted.”'
                  : '“I moved to my sister\'s place in Kayole last year and the old station is two matatus away.”'}
              />
              <p className="text-erp-xs text-muted-foreground mt-1">
                Stored verbatim and never normalised. If they said it, write it as they said it.
              </p>
            </label>

            <label className="block">
              <span className="text-erp-xs text-muted-foreground">
                Your summary for the reviewer <span className="text-red-600">*</span>
              </span>
              <textarea
                className="erp-input w-full mt-1 min-h-[72px]"
                value={rationale}
                onChange={(e) => setRationale(e.target.value)}
                placeholder={registering
                  ? 'Met at the Kayole civic drive; ID verified against eCitizen, lives 600m from the station, has never been registered.'
                  : 'Residence confirmed at the door; the ward boundary puts her 4km from her registered station and 600m from Kayole Primary.'}
              />
              <p className={cn('text-erp-xs mt-1',
                rationale.trim().length > 0 && rationale.trim().length < 20
                  ? 'text-amber-700 dark:text-amber-400' : 'text-muted-foreground')}>
                At least 20 characters. Whoever approves this reads only what you write here.
              </p>
            </label>

            <Note tone="info">
              Next: capture consent on the request. Until then it stays a draft — the API and the
              database both refuse to submit a request without one.
            </Note>
          </>
        )}
      </div>
    </Modal>
  )
}

/**
 * Who the ID belongs to, and what that means for the work.
 *
 * Four outcomes, each of which changes what the operator can do next, so each gets its
 * own shape rather than a status word in a shared card.
 */
function SubjectCard({
  lookup, useUpstream, onUseUpstream, residenceWardId, residenceWardLabel, onPickWard,
}: {
  lookup: TransferLookupResponse
  useUpstream: boolean
  onUseUpstream: (v: boolean) => void
  residenceWardId: string
  residenceWardLabel: string
  onPickWard: (id: string, label: string) => void
}) {
  if (lookup.found) {
    const unregistered = lookup.registrationStatusCode === 'UNREGISTERED'
    const placement = placementOf(lookup)
    return (
      <div className="erp-card p-3 space-y-2">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="text-erp-sm font-medium flex items-center gap-2 flex-wrap">
              <BadgeCheck className="h-3.5 w-3.5 text-emerald-600 shrink-0" />
              {lookup.displayName ?? 'On our register'}
              {lookup.voterNumber && (
                <span className="font-mono text-erp-xs text-muted-foreground">{lookup.voterNumber}</span>
              )}
            </div>
            <p className="text-erp-xs text-muted-foreground mt-0.5">
              {unregistered
                ? 'On our records, but not on the IEBC roll. This is a registration, not a transfer.'
                : 'Already on the register — this will be a transfer request.'}
            </p>
          </div>
        </div>

        {placement.length > 0 ? (
          <div>
            <div className="text-erp-xs text-muted-foreground mb-1">
              {unregistered ? 'Lives in' : 'Registered at'}
            </div>
            <PlacementLine placement={placement} />
          </div>
        ) : (
          <p className="text-erp-xs text-muted-foreground">No polling station on record.</p>
        )}

        {lookup.openTransferRequestId && (
          <Note tone="bad">
            {lookup.openTransferReference ?? 'A request'} is already open for this person
            {lookup.openTransferStatus
              ? ` (${TRANSFER_STATUS_LABEL[lookup.openTransferStatus] ?? humanise(lookup.openTransferStatus)})`
              : ''}
            . One person cannot have two open requests — finish or withdraw{' '}
            <a className="underline" href={`/transfers/${lookup.openTransferRequestId}`}>
              the existing one
            </a>{' '}
            first.
          </Note>
        )}
      </div>
    )
  }

  if (!lookup.upstream?.found) return null

  const u = lookup.upstream
  const name = [u.firstName, u.middleName, u.surname].filter(Boolean).join(' ')
  return (
    <div className="erp-card p-3 space-y-2">
      <div className="text-erp-sm font-medium flex items-center gap-2 flex-wrap">
        <UserPlus className="h-3.5 w-3.5 text-chocolate shrink-0" />
        {name || 'Known to the identity service'}
        {u.verified && (
          <span className="text-erp-xs px-1.5 py-0.5 rounded border border-emerald-400/60 text-emerald-700 dark:text-emerald-400">
            government-verified
          </span>
        )}
      </div>
      <p className="text-erp-xs text-muted-foreground">
        Not on our register and not on the IEBC roll. {u.birthYear ? `Born ${u.birthYear}. ` : ''}
        Recording them here creates a citizen record so the registration request has something to
        hang on — it does not register anyone.
      </p>

      {!useUpstream ? (
        <button type="button" className="erp-btn erp-btn-primary" onClick={() => onUseUpstream(true)}>
          <Check className="h-3.5 w-3.5" /> Record this citizen and assist registration
        </button>
      ) : (
        <div className="space-y-2">
          <WardPicker
            value={residenceWardId}
            label={residenceWardLabel}
            onPick={onPickWard}
          />
          <p className="text-erp-xs text-muted-foreground">
            The ward they live in. Required: it is the only geographic anchor an unregistered
            citizen has, and every scope rule in the system reads it.
          </p>
          <button
            type="button"
            className="text-erp-xs text-muted-foreground hover:text-foreground"
            onClick={() => onUseUpstream(false)}
          >
            Don't use this result
          </button>
        </div>
      )}
    </div>
  )
}

/** County → Constituency → Ward → Station, as the hierarchy it is. */
export function PlacementLine({ placement }: { placement: { level: string; name: string }[] }) {
  if (placement.length === 0) return <span className="text-muted-foreground">—</span>
  return (
    <div className="flex items-center gap-1 flex-wrap">
      {placement.map((p, i) => (
        <span key={`${p.level}-${i}`} className="inline-flex items-center gap-1">
          <span className={cn(
            'text-erp-xs px-1.5 py-0.5 rounded border border-border',
            i === placement.length - 1 && 'font-semibold border-chocolate/50 bg-chocolate-soft/30',
          )}>
            {p.name}
          </span>
          {i < placement.length - 1 && (
            <ArrowRight className="h-3 w-3 text-muted-foreground/50 shrink-0" />
          )}
        </span>
      ))}
    </div>
  )
}

function VoterPicker({
  value, label, loading: resolving, onPick,
}: {
  value: string; label: string; loading?: boolean
  onPick: (id: string, label: string) => void
}) {
  const [q, setQ] = useState('')
  const debounced = useDebounced(q, 300)
  const [results, setResults] = useState<{ id: string; displayName: string }[]>([])
  const [loading, setLoading] = useState(false)

  /**
   * If what was typed is a national ID, it must not be searched here: `listVoters` is a
   * GET, so the number would ride in a query string — the one thing this feature refuses
   * to do — and the debounce would send every prefix of it as well. The box says so and
   * offers the door that handles IDs properly.
   */
  const looksLikeId = /^\d{6,}$/.test(debounced.trim())

  useEffect(() => {
    if (looksLikeId || debounced.trim().length < 2) { setResults([]); return }
    let cancelled = false
    setLoading(true)
    listVoters({ q: debounced } as never, undefined, 8)
      .then((page) => { if (!cancelled) setResults(page.content as never) })
      .catch(() => { if (!cancelled) setResults([]) })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [debounced, looksLikeId])

  return (
    <>
      <Picked
        title="Voter"
        value={value}
        label={label}
        onClear={() => onPick('', '')}
        placeholder="Search the register by name"
        q={q}
        setQ={setQ}
        loading={loading || Boolean(resolving)}
        options={results.map((v) => ({ id: v.id, label: v.displayName, sub: '' }))}
        onPick={onPick}
      />
      {!value && looksLikeId && (
        <p className="text-erp-xs text-amber-700 dark:text-amber-400 mt-1 flex items-start gap-1.5">
          <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-px" />
          That looks like an ID number, and this box searches names — an ID typed here
          would travel in a URL. Use "Search by national ID instead".
        </p>
      )}
      {!value && !looksLikeId && (
        <p className="text-erp-xs text-amber-700 dark:text-amber-400 mt-1 flex items-start gap-1.5">
          <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-px" />
          Names are shared. Confirm the ID on the request before anyone approves it.
        </p>
      )}
    </>
  )
}

export function StationPicker({
  value, label, onPick, title = 'Target polling station',
}: {
  value: string; label: string; title?: string
  onPick: (id: string, label: string) => void
}) {
  const [q, setQ] = useState('')
  const debounced = useDebounced(q, 300)
  const [results, setResults] = useState<UnitResponse[]>([])
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (debounced.trim().length < 2) { setResults([]); return }
    let cancelled = false
    setLoading(true)
    listUnits({ q: debounced, type: ['POLLING_STATION'] } as never, 0, 8)
      .then((page) => { if (!cancelled) setResults(page.content) })
      .catch(() => { if (!cancelled) setResults([]) })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [debounced])

  return (
    <Picked
      title={title}
      value={value}
      label={label}
      onClear={() => onPick('', '')}
      placeholder="Search stations by name or code"
      q={q}
      setQ={setQ}
      loading={loading}
      options={results.map((u) => ({ id: u.id, label: u.name, sub: u.code }))}
      onPick={onPick}
    />
  )
}

function WardPicker({
  value, label, onPick,
}: { value: string; label: string; onPick: (id: string, label: string) => void }) {
  const [q, setQ] = useState('')
  const debounced = useDebounced(q, 300)
  const [results, setResults] = useState<UnitResponse[]>([])
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (debounced.trim().length < 2) { setResults([]); return }
    let cancelled = false
    setLoading(true)
    listUnits({ q: debounced, type: ['WARD'] } as never, 0, 8)
      .then((page) => { if (!cancelled) setResults(page.content) })
      .catch(() => { if (!cancelled) setResults([]) })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [debounced])

  return (
    <Picked
      title="Ward of residence"
      value={value}
      label={label}
      onClear={() => onPick('', '')}
      placeholder="Search wards by name or code"
      q={q}
      setQ={setQ}
      loading={loading}
      options={results.map((u) => ({ id: u.id, label: u.name, sub: u.code }))}
      onPick={onPick}
    />
  )
}

/** Shared shell so the pickers behave identically — they are the same control. */
function Picked({
  title, value, label, onClear, placeholder, q, setQ, loading, options, onPick,
}: {
  title: string
  value: string
  label: string
  onClear: () => void
  placeholder: string
  q: string
  setQ: (v: string) => void
  loading: boolean
  options: { id: string; label: string; sub?: string }[]
  onPick: (id: string, label: string) => void
}) {
  return (
    // `data-picker` is the handle the browser tests grab. Three of these controls can be
    // open in one dialog and they are deliberately identical, so "the first option in the
    // first card" is ambiguous in a way that silently tests the wrong control.
    <div data-picker={title}>
      <span className="text-erp-xs text-muted-foreground">
        {title} <span className="text-red-600">*</span>
      </span>
      {value ? (
        <div className="mt-1 flex items-center gap-2 px-3 h-9 border border-chocolate/50 rounded-md bg-chocolate-soft/30">
          <Check className="h-3.5 w-3.5 text-chocolate shrink-0" />
          <span className="text-erp-sm truncate flex-1">{label}</span>
          <button type="button" className="text-erp-xs text-muted-foreground hover:text-foreground shrink-0"
                  onClick={onClear}>
            Change
          </button>
        </div>
      ) : (
        <>
          <div className="mt-1 flex items-center gap-2 px-3 h-9 border border-input rounded-md bg-background">
            <Search className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={placeholder}
              aria-label={title}
              className="bg-transparent outline-none text-erp-sm w-full min-w-0"
            />
            {loading && <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground shrink-0" />}
          </div>
          {options.length > 0 && (
            <div className="mt-1 erp-card max-h-40 overflow-y-auto py-1">
              {options.map((o) => (
                <button
                  key={o.id}
                  type="button"
                  onClick={() => onPick(o.id, o.sub ? `${o.label} (${o.sub})` : o.label)}
                  className="w-full px-3 py-1.5 text-left text-erp-sm hover:bg-muted"
                >
                  {o.label}
                  {o.sub && <span className="text-erp-xs text-muted-foreground font-mono ml-2">{o.sub}</span>}
                </button>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  )
}
